import { useTranslations } from "next-intl";
import type { Filter } from "@/types/Filter";
import type { Field } from "@/types/Field";
import { DataType } from "@/types/enums/DataType";
import { formatToStringFromDataType } from "@/lib/utils/parser";
import { OptionChip, isValidOptionColor } from "@/lib/utils/optionChipColor";
import {
  bareFieldName,
  findFieldByKey,
} from "@/components/ConditionBuilder/condition-utils";
import { useEventTimezone } from "@/providers/EventProvider";

// System enum / select values are translated via the optionsMultiSingleSelect
// namespace; everything else is formatted by data type.
const TRANSLATABLE_TYPES = new Set([
  DataType.GUEST_STATUS,
  DataType.EMAIL_STATUS,
  DataType.SINGLE_SELECT,
  DataType.MULTI_SELECT,
]);

/**
 * Read-only render of a saved filter's conditions as chips —
 * `operator value (Feldname)`, mirroring the guest list's FilterGrid look.
 *
 * Deliberately does NOT use FilterGrid (edit-bound, needs MetaTableProvider) or
 * `parseToAdvancedFilter` (mutates the shared `allFields[].translation`, which
 * would clobber the editor's own labels). Values are formatted by a local helper
 * that mirrors FilterGrid's `formatFilterValue`.
 *
 * Resolution goes through `findFieldByKey`, and there is deliberately NO
 * `fieldLabelMap` prop: the keys this renders demonstrably arrive in BOTH shapes
 * (a saved guest-list filter stores the bare custom name, a role's row filter the
 * `additionalFields.` wire key), and a map keyed by only one of them mislabels
 * the other — or, keyed by both, is ambiguous when a custom field shares a
 * built-in's name. The component already receives `allFields`, which is the
 * unambiguous source.
 */
export function FilterConditionsPreview({
  filters,
  allFields,
  labelFor,
}: {
  filters: Filter[];
  allFields: Field[];
  /** Display label for a resolved built-in field (i18n lives in the host). */
  labelFor: (field: Field) => string;
}) {
  const t2 = useTranslations("optionsMultiSingleSelect");
  // Root translator for `Field.optionNamespace`, resolved by dotted path.
  const tRoot = useTranslations();
  const tRules = useTranslations("rulesAndLimits");
  const eventTz = useEventTimezone();

  // Mirrors FilterGrid.formatFilterValue (PFilter/components/FilterGrid.tsx).
  //
  // ⚠ The claim above is only worth making if it stays true. It had already
  // drifted once: FilterGrid gained an `optionNamespace` parameter and this
  // clone did not, so a field pointing at its own catalogue would have rendered
  // correctly in the guest list and wrongly here — under a comment asserting
  // the two agree. Harmless at the time (role templates resolve guest fields,
  // none of which carry a namespace), which is exactly why nobody would have
  // noticed. Kept in step deliberately.
  //
  // ⚠ AUDIT 2026-08-15 — and the count was wrong. There are THREE copies of
  // this FUNCTION, not two: `PExportCheckInLogs.tsx` carries a
  // fourth-parameter version that never received `optionNamespace` either.
  //
  // ⚠ AUDIT 2026-08-15, second pass — and "three" is still too narrow, which is
  // the joke this file cannot stop telling. Three is the count of functions
  // SHAPED like this one. The same job — resolve a stored filter value through
  // `optionLabels`, then the shared catalogue, then `formatToStringFromDataType`
  // — is also done by `ConditionBuilder/condition-display.ts` and
  // `ConditionBuilder/ConditionRow.tsx`, neither of which takes an
  // `optionNamespace` and neither of which has the `tRoot.has` fallback, so a
  // miss there prints the DOTTED KEY into the UI. Five sites, not three.
  //
  // Nothing is wrong today at any of them (the surfaces that mount them resolve
  // guest / check-in fields, and only `FIELDS_ACTION_LOG` carries a namespace).
  // The point is the shape of the mistake: a comment written to correct a
  // miscount asserted a new count without checking the class, twice. It is left as it is on purpose rather
  // than threaded through — no field on the check-in lane carries a namespace
  // today (only `FIELDS_ACTION_LOG` does), so the parameter would arrive
  // permanently `undefined`, and a fix with nothing to do is indistinguishable
  // from coverage. The note now sits in that file too, where whoever adds the
  // first namespaced check-in field will meet it.
  //
  // Recording the miscount and not just the fix: "kept in step" was written
  // about a set whose size had not been checked.
  const formatValue = (
    value: string | string[],
    dataType: DataType,
    additionalField?: boolean,
    // id→name map for optionSource fields (Raum/Angebot/E-Mail-Vorlage) so the
    // chip shows the human name instead of the stored ObjectId.
    optionLabels?: Record<string, string>,
    optionNamespace?: string,
  ): string => {
    if (TRANSLATABLE_TYPES.has(dataType)) {
      // `has` first: a root-translator miss returns the DOTTED key, which is
      // worse output than the bare value. Same reasoning as FilterGrid.
      const translate = (v: string) => {
        if (optionNamespace === undefined) return t2(v);
        const key = `${optionNamespace}.${v}`;
        return tRoot.has(key) ? tRoot(key) : v;
      };
      const label = (v: string) =>
        optionLabels?.[v] ?? (additionalField ? v : translate(v));
      if (Array.isArray(value)) {
        return value.map(label).join(", ");
      }
      if (value === null || value === undefined || value === "") return "";
      return label(value);
    }
    return formatToStringFromDataType(value, dataType, t2("clock"), eventTz);
  };

  return (
    <ul className="flex flex-row flex-wrap gap-1.5">
      {filters.map((f, idx) => {
        const def = findFieldByKey(allFields, f.key);
        const dataType = def?.dataType ?? DataType.STRING;
        const additionalField = def?.additionalField;
        const colors = def?.optionColors;
        // Unresolvable = its field was deleted: the shared marker, never the raw
        // wire key.
        const label = def
          ? labelFor(def)
          : tRules("deletedFieldLabel", { name: bareFieldName(f.key) });
        const isColoredSelect =
          additionalField &&
          (dataType === DataType.SINGLE_SELECT ||
            dataType === DataType.MULTI_SELECT) &&
          !!colors;
        return (
          <li
            key={`${f.key}-${f.operator}-${Array.isArray(f.value) ? f.value.join(",") : f.value}-${idx}`}
            className="flex flex-row items-center h-min border outline-quaternary py-1 px-2 rounded-lg w-max"
          >
            <span className="body-3-regular">
              {f.operator}{" "}
              {isColoredSelect ? (
                <span className="inline-flex flex-wrap items-center gap-1 align-middle">
                  {(Array.isArray(f.value) ? f.value : [f.value])
                    .filter((v) => v !== null && v !== undefined && v !== "")
                    .map((v) =>
                      isValidOptionColor(colors?.[v]) ? (
                        <OptionChip key={v} label={v} color={colors?.[v]} />
                      ) : (
                        <span key={v}>{v}</span>
                      ),
                    )}
                </span>
              ) : (
                formatValue(
                  f.value,
                  dataType,
                  additionalField,
                  def?.optionLabels,
                  def?.optionNamespace,
                )
              )}
              <span className="body-4-regular font-tertiary">
                {" ("}
                {label}
                {")"}
              </span>
            </span>
          </li>
        );
      })}
    </ul>
  );
}
